import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import * as React from "react";

import { RobotoIconButton } from "../RobotoIconButton";
import { RobotoTooltip } from "../RobotoTooltip";

import styles from "./ShareableLinkField.module.css";

interface ShareableLinkFieldProps {
  /** The URL/link to display and share */
  link: string;
  /** Optional className for the container */
  className?: string;
}

export const ShareableLinkField: React.FC<ShareableLinkFieldProps> = ({
  link,
  className,
}) => {
  const [copyTooltip, setCopyTooltip] = React.useState("Copy link");

  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopyTooltip("Copied!");
        setTimeout(() => setCopyTooltip("Copy link"), 2000);
      })
      .catch(() => {
        setCopyTooltip("Failed to copy");
      });
  };

  const handleOpenInNewTab = () => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const containerClassName = className
    ? `${styles.container} ${className}`
    : styles.container;

  return (
    <div className={containerClassName}>
      <input
        type="text"
        value={link}
        readOnly
        autoFocus
        className={styles.linkInput}
        onFocus={(e) => {
          e.target.select();
          // Browsers don't reset the scroll position when select() is called.
          // For long URLs that overflow the input, the viewport may be scrolled
          // to the end of the text. Explicitly resetting scrollLeft ensures the
          // user sees the beginning of the URL after focusing.
          e.target.scrollLeft = 0;
        }}
      />
      <RobotoTooltip title={copyTooltip}>
        <span>
          <RobotoIconButton
            className={styles.actionButton}
            onClick={handleCopyClick}
            eventName="CopyWorkspaceLinkClicked"
            eventProperties={{ link }}
          >
            <ContentCopyIcon className={styles.actionIcon} />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>
      <RobotoTooltip title="Open in new tab">
        <span>
          <RobotoIconButton
            className={styles.actionButton}
            onClick={handleOpenInNewTab}
            eventName="OpenWorkspaceLinkInNewTabClicked"
            eventProperties={{ link }}
          >
            <OpenInNewIcon className={styles.actionIcon} />
          </RobotoIconButton>
        </span>
      </RobotoTooltip>
    </div>
  );
};
